import React, { useEffect, useState, useRef } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import GOQiiButton from "../Components/GoqiiButton";
import "./CaloriesCard.css";
import SectionHeader from "../Components/SectionHeader";
import { useTranslation } from "react-i18next";
import { lifestyleApi } from "../apiService/api";
import { getGoqiiUserId } from "../apiService/timespam";
import { triggerShare, navigateWithFai } from "../utils/Utils";



// --- Shimmer helpers ---
const ensureShimmerKeyframes = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById("shimmer-keyframes")) return;

  const style = document.createElement("style");
  style.id = "shimmer-keyframes";
  style.innerHTML = `
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
  `;
  document.head.appendChild(style);
};

const shimmerBaseStyle = {
  position: "relative",
  overflow: "hidden",
  backgroundImage:
    "linear-gradient(90deg, var(--shimmer-1) 0%, var(--shimmer-2) 50%, var(--shimmer-1) 100%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.3s linear infinite",
};

const CaloriesSkeleton = () => {
  useEffect(() => {
    ensureShimmerKeyframes();
  }, []);

  return (
    <div
      style={{
        width: "100%",
        backgroundColor: "var(--bg-card)",
        borderRadius: 20,
        padding: 12,
        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
      }}
    >
      {/* Title */}
      <div style={{ marginBottom: 12 }}>
        <div
          style={{
            ...shimmerBaseStyle,
            height: 18,
            width: "60%",
            borderRadius: 8,
          }}
        />
      </div>

      {/* Chart area */}
      <div
        style={{
          ...shimmerBaseStyle,
          height: 260,
          borderRadius: 16,
        }}
      />

      {/* Button */}
      <div
        style={{
          marginTop: 24,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            ...shimmerBaseStyle,
            height: 40,
            width: 180,
            borderRadius: 24,
          }}
        />
      </div>
    </div>
  );
};


const CaloriesCard = ({ shareConfig } = {}) => {
  const { t } = useTranslation();
  const chartRef = useRef(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    const goqiiUserId = getGoqiiUserId();
    lifestyleApi
      .weeklyCalories(goqiiUserId)
      .then((res) => {
        if (res?.data?.code === 200) {
          setData(res.data.data);
        }
      })
      .catch((err) => console.error("Error fetching calories:", err));
  }, []);

  if (!data) return <CaloriesSkeleton />;

  // Convert string calories to Numbers for Highcharts
  const caloriesArray = data.calories.map((v) => Number(v));

  const options = {
    chart: {
      type: "column",
      backgroundColor: "transparent",
      marginTop: 15,
      marginBottom: 80,
      spacingLeft: 5,
      spacingRight: 10,
      height: 300, // Adjusted for better visibility on all devices
      style: { fontFamily: "'Open Sans', sans-serif" },
      events: {
        render() {
          const chart = this;
          const isSmallMobile = chart.chartWidth < 480;

          if (chart.customElements) {
            chart.customElements.forEach((el) => el.destroy());
          }
          chart.customElements = [];

          // Custom Label for Target Calories on the right side
          const labelText = `Target Calories: ${data.caloriesTarget} cal`;
          const label = chart.renderer
            .text(
              labelText,
              chart.chartWidth - 20, // Right align
              chart.yAxis[0].toPixels(data.caloriesTarget) - 9 // Position slightly above the line
            )
            .css({
              fontSize: isSmallMobile ? "11px" : "15px",
              color: "var(--text-primary)",
              fontWeight: "600"
            })
            .attr({ align: "right", zIndex: 6 })
            .add();

          chart.customElements.push(label);
        },
      },
    },

    // title: {
    //   text: "Last 7 Days Calorie Intake",
    //   align: "left",
    //   x: 3,
    //   style: {
    //     fontSize: "16px",
    //     fontWeight: "700",
    //     color: "var(--text-primary)"
    //   },
    // },
    title: { text: null },

    xAxis: {
      categories: data.logDates,
      lineColor: "var(--text-muted)",
      lineWidth: 0,
      tickWidth: 0,
      labels: {
        y: 14,
        style: {
          fontSize: "12px",
          color: "var(--text-primary)"
        },
      },
    },

    yAxis: {
      title: { text: null },
      min: 0,
      max: 3000,
      tickInterval: 1000,
      gridLineColor: "var(--text-muted)",
      labels: {
        formatter: function () {
          return this.value === 0 ? "0" : this.value / 1000 + "k";
        },
        style: { color: "var(--text-primary)", fontSize: "11px" }
      },
      plotLines: [
        {
          value: data.caloriesTarget,
          color: "#4A90E2", // Blue line
          dashStyle: "Dash",
          width: 2,
          zIndex: 5,
        },
      ],
    },

    tooltip: {
      enabled: false,
      headerFormat: "",      // removes category/index header
      backgroundColor: "var(--bg-surface)",
      borderWidth: 0,
      shadow: true,
      style: {
        color: "var(--text-primary)",
        fontWeight: "600",
        fontSize: "12px"
      },
      formatter: function () {
        return `${this.y} cal`;
      }
    },


    legend: { enabled: false },

    plotOptions: {
      column: {
        borderWidth: 0,
        borderRadius: 4,
        pointWidth: 14, // Slim bars like screenshot
        color: "#28c1b8", // Teal color
        states: {
          hover: { color: "#1fa39b" }
        }
      },
    },

    series: [
      {
        name: t("calories.calories"),
        data: caloriesArray,
      },
    ],

    credits: { enabled: false },

    // Responsive Rules
    responsive: {
      rules: [
        {
          condition: { maxWidth: 500 },
          chartOptions: {
            chart: { height: 300 },
            xAxis: {
              labels: {
                y: 9,
                rotation: -45,          // 👈 make them cross (diagonal)
                align: "right",
                style: { fontSize: "10px" }
              }
            },
            plotOptions: {
              column: { pointWidth: 10 }
            }
          }
        },
        {
          condition: { maxWidth: 380 },
          chartOptions: {
            xAxis: {
              labels: {
                y: 9,
                rotation: -60,          // more angle for very small screens
                style: { fontSize: "9px" }
              }
            }
          }
        }
      ]
    }

  };

  return (
    <div className="calorie-card-container">
      <SectionHeader
        icon="https://goqii-app.s3.amazonaws.com/emailer/65771_1594801574.png"
        iconStyle={{ filter: "grayscale(1)" }}
        title={data.title || t("calories.title")}
        infoIconLight="https://appcdn.goqii.com/storeimg/38298_1764052060.png"
        infoIconDark="https://appcdn.goqii.com/storeimg/26151_1769593659.png"
        showInfo={!!data.showInfo}
        onInfoClick={() => {
          navigateWithFai(data.title || t("calories.title"), data.infoUrl);
        }}
        showShare={!!shareConfig?.isSharable}
        onShareClick={() => triggerShare(shareConfig?.text)}
      />
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartRef}
      />

      <div className="calorie-btn-container">
        <GOQiiButton
          text={t("calories.viewInsights")}
          color="green"
          solid={true}
          shape="rounded_edges"
          font="bold"
          onClick={() => window.open(data.deepLink, "_blank")}
        />
      </div>
    </div>
  );
};

export default CaloriesCard;